<template>
    <v-container class="profile-container mb-12 mb-lg-0">
        <v-overlay :value="loading">
            <v-progress-circular indeterminate width="6" size="48"></v-progress-circular>
        </v-overlay>
        <v-row justify="center">
            <v-col cols="12" lg="10" xl="8" class="profile-main-col">
                <v-alert v-if="user.suspendReason !== null" text prominent type="error" icon="mdi-comment-alert">
                    <span class="pre_wrap">{{ user.suspendReason }}</span>
                </v-alert>

                <v-card color="secondary" flat class="profile-hero-card mb-4" style="border-radius: 10px;">
                    <v-card-text>
                        <v-row align="center">
                            <v-col cols="12" sm="4" md="3" class="profile-avatar-col text-center">
                                <v-avatar :size="$vuetify.breakpoint.xsOnly ? 84 : 112" class="mb-3" style="border: 2px solid #0D93A5">
                                    <img :src="profilePhotoSrc" :alt="displayName || 'Perfil'"/>
                                </v-avatar>
                                <v-file-input
                                    v-if="editMode"
                                    v-model="form.photo"
                                    :label="labels.photo"
                                    name="photo"
                                    accept="image/*"
                                    capture="user"
                                    dense
                                    outlined
                                    hide-details="auto"
                                    :error-messages="errors.photo"
                                    :rules="photoRules"
                                    :disabled="loading"
                                    prepend-icon="mdi-camera-image"
                                    @input="clearErrors('photo')"
                                    @click:clear="clearImage(1)"
                                    show-size/>
                            </v-col>
                            <v-col cols="12" sm="8" md="9" class="profile-info-col">
                                <div class="d-flex flex-column flex-sm-row align-sm-center">
                                    <div class="flex-grow-1">
                                        <div class="profile-display-name text-h5 font-weight-bold primary--text mb-1">{{ displayName || 'Mi perfil' }}</div>
                                        <div class="subtitle-2 primary--text font-weight-bold mb-2">
                                            {{ accountTag }}
                                            <v-btn
                                                v-if="canCustomizeNickname"
                                                x-small
                                                text
                                                color="primary"
                                                class="ml-1"
                                                @click="openNicknameDialog">
                                                Cambiar
                                            </v-btn>
                                        </div>
                                        <v-chip small :color="statusMeta.color" dark class="mr-1 mb-1">
                                            <v-icon left small>{{ statusMeta.icon }}</v-icon>{{ statusMeta.text }}
                                        </v-chip>
                                        <v-chip small color="primary" class="secondary--text mr-1 mb-1" v-if="form.citizenship">
                                            {{ form.citizenship }}
                                        </v-chip>
                                        <v-chip small outlined color="primary" class="mr-1 mb-1" v-if="form.td && form.nd">
                                            {{ form.td }} {{ form.nd }}
                                        </v-chip>
                                    </div>
                                    <div class="mt-3 mt-sm-0 profile-hero-actions">
                                        <v-btn
                                            v-if="!editMode"
                                            color="primary"
                                            class="secondary--text"
                                            depressed
                                            :block="$vuetify.breakpoint.xsOnly"
                                            @click="editMode = true">
                                            <v-icon left>mdi-pencil</v-icon>Editar
                                        </v-btn>
                                        <v-btn
                                            v-else
                                            color="error"
                                            outlined
                                            :block="$vuetify.breakpoint.xsOnly"
                                            @click="cancelEdit"
                                            :disabled="loading">
                                            <v-icon left>mdi-close-circle</v-icon>Cancelar
                                        </v-btn>
                                    </div>
                                </div>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>

                <v-alert v-if="editMode" text type="info" icon="mdi-shield-sync">
                    Al guardar cambios, tus datos quedaran en revision hasta que administracion los apruebe.
                </v-alert>

                <v-card class="profile-section-card mb-4" v-if="identityLoading || identity.enabled || identityDocuments.length">
                    <v-card-title class="subtitle-1 font-weight-bold primary--text d-flex flex-wrap align-center" style="word-break: normal">
                        <v-icon color="primary" class="mr-2">mdi-card-account-details-star</v-icon>
                        Identidad y documentos
                        <v-spacer/>
                        <v-btn icon color="primary" :loading="identityLoading" :disabled="identityLoading" @click="getIdentity">
                            <v-icon>mdi-sync</v-icon>
                        </v-btn>
                        <v-btn
                            v-if="identity.enabled"
                            small
                            depressed
                            color="primary"
                            class="secondary--text ml-sm-2 mt-2 mt-sm-0"
                            :block="$vuetify.breakpoint.xsOnly"
                            :loading="identityLoading"
                            :disabled="identityActionDisabled"
                            @click="openIdentity">
                            <v-icon left small>mdi-shield-check</v-icon>{{ identityButtonText }}
                        </v-btn>
                    </v-card-title>
                    <v-card-text>
                        <v-skeleton-loader v-if="identityLoading && !identityReady" type="list-item-two-line"/>
                        <template v-else>
                            <v-alert v-if="identity.enabled && identityBlockedReason" dense text type="warning" class="mb-3" icon="mdi-account-edit">
                                {{ identityBlockedReason }}
                                <div v-if="!editMode && !identityProfileReady" class="mt-2">
                                    <v-btn small outlined color="warning" @click="editMode = true">
                                        <v-icon left small>mdi-pencil</v-icon>Completar datos
                                    </v-btn>
                                </div>
                            </v-alert>
                            <v-row dense class="mb-2">
                                <v-col cols="12" sm="6">
                                    <v-sheet outlined rounded class="pa-3 profile-identity-summary">
                                        <div class="caption text-uppercase font-weight-bold primary--text">Estado de identidad</div>
                                        <v-chip small dark class="mt-2" :color="identityStatusMeta.color">
                                            <v-icon left small>{{ identityStatusMeta.icon }}</v-icon>{{ identityStatusMeta.text }}
                                        </v-chip>
                                    </v-sheet>
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-sheet outlined rounded class="pa-3 profile-identity-summary">
                                        <div class="caption text-uppercase font-weight-bold primary--text">Documentos aprobados</div>
                                        <div class="text-h6 font-weight-bold primary--text mt-1">
                                            {{ approvedIdentityDocuments.length }}
                                        </div>
                                    </v-sheet>
                                </v-col>
                            </v-row>
                            <v-alert v-if="!identityDocuments.length" dense text type="info" class="mb-0">
                                Aun no tienes documentos validados. Puedes validar documento nacional, pasaporte u otro documento propio.
                            </v-alert>
                            <v-row dense v-else>
                                <v-col v-for="document in identityDocuments" :key="'identity-document-' + document.id" cols="12" md="6">
                                    <v-sheet outlined rounded class="pa-3 profile-identity-document">
                                        <div class="d-flex align-center mb-2">
                                            <v-icon :color="documentStatusColor(document.status)" class="mr-2">
                                                {{ documentStatusIcon(document.status) }}
                                            </v-icon>
                                            <div class="font-weight-bold primary--text flex-grow-1">
                                                {{ document.label || document.document_type }}
                                            </div>
                                            <v-chip x-small dark :color="documentStatusColor(document.status)">
                                                {{ document.status_label }}
                                            </v-chip>
                                        </div>
                                        <div class="body-2">
                                            <strong>Titular:</strong> {{ document.full_name || displayName || 'Sin nombre' }}
                                        </div>
                                        <div class="body-2" v-if="document.birthday">
                                            <strong>Nacimiento:</strong> {{ formatDate(document.birthday) }}
                                        </div>
                                        <div class="caption grey--text text--darken-1 mt-1">
                                            Registrado: {{ document.created_at }}
                                        </div>
                                        <v-alert v-if="document.review_note" dense text type="warning" class="mt-2 mb-0">
                                            {{ document.review_note }}
                                        </v-alert>
                                    </v-sheet>
                                </v-col>
                            </v-row>
                        </template>
                    </v-card-text>
                </v-card>

                <v-form ref="form" @submit.prevent="submit" lazy-validation v-model="valid">
                    <v-card class="profile-section-card mb-4">
                        <v-card-title class="subtitle-1 font-weight-bold primary--text">
                            <v-icon color="primary" class="mr-2">mdi-account-circle</v-icon>Datos personales
                        </v-card-title>
                        <v-card-text>
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        :label="labels.name"
                                        type="text"
                                        name="name"
                                        :readonly="!editMode"
                                        v-model="form.name"
                                        :error-messages="errors.name"
                                        :rules="[rules.required('name'), rules.max('name', 120, 'El')]"
                                        :disabled="loading"
                                        @input="clearErrors('name')"
                                        prepend-icon="mdi-account-circle"/>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        :label="labels.lname"
                                        type="text"
                                        name="lname"
                                        :readonly="!editMode"
                                        v-model="form.lname"
                                        :error-messages="errors.lname"
                                        :rules="[rules.required('lname'), rules.max('lname', 120, 'El')]"
                                        :disabled="loading"
                                        @input="clearErrors('lname')"
                                        prepend-icon="mdi-account"/>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        :label="labels.email"
                                        type="email"
                                        name="email"
                                        :readonly="!editMode"
                                        v-model="form.email"
                                        :error-messages="errors.email"
                                        :rules="[rules.required('email'), rules.mail('email')]"
                                        :disabled="loading"
                                        @input="clearErrors('email')"
                                        prepend-icon="mdi-email"/>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        :label="labels.phone"
                                        v-model="form.phone"
                                        type="tel"
                                        name="phone"
                                        :readonly="!editMode"
                                        placeholder="+573101234567"
                                        :error-messages="errors.phone"
                                        :rules="[rules.required('phone')]"
                                        :disabled="loading"
                                        prepend-icon="mdi-phone"
                                        @input="clearErrors('phone')"/>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-dialog
                                        ref="menu1"
                                        v-model="menu.birthday"
                                        :close-on-content-click="false"
                                        transition="scale-transition"
                                        offset-y
                                        width="290px">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-text-field
                                                :label="labels.birthday"
                                                v-model="birthday"
                                                name="birthday"
                                                prepend-icon="mdi-calendar"
                                                :error-messages="errors.birthday"
                                                :rules="[rules.req('birthday')]"
                                                :disabled="loading"
                                                readonly
                                                v-bind="attrs"
                                                v-on="editMode ? on : {}"/>
                                        </template>
                                        <v-date-picker
                                            v-if="editMode"
                                            v-model="form.birthday"
                                            :active-picker.sync="activePicker"
                                            scrollable
                                            :max="adultMaxDate"
                                            min="1950-01-01"
                                            @change="menu.birthday = false"/>
                                    </v-dialog>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>

                    <v-card class="profile-section-card mb-4">
                        <v-card-title class="subtitle-1 font-weight-bold primary--text">
                            <v-icon color="primary" class="mr-2">mdi-card-account-details</v-icon>Identificacion
                        </v-card-title>
                        <v-card-text>
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-autocomplete
                                        :label="labels.citizenship"
                                        :items="items"
                                        name="citizenship"
                                        :readonly="!editMode"
                                        v-model="form.citizenship"
                                        :error-messages="errors.citizenship"
                                        :disabled="loading"
                                        :rules="[rules.required('citizenship')]"
                                        @select="clearErrors('citizenship')"
                                        prepend-icon="mdi-map-marker"
                                        item-text="name"
                                        item-value="code">
                                        <template v-slot:selection="{ item }">
                                            <v-flex>
                                                <v-avatar size="28px">
                                                    <img :src="'https://flagcdn.com/h240/' + item.img + '.png'" :alt="item.name.toLowerCase()" />
                                                </v-avatar>
                                                <span class="ml-1">{{ item.name }}</span>
                                            </v-flex>
                                        </template>
                                        <template v-slot:item="{ item }">
                                            <v-list-item-avatar>
                                                <img :src="'https://flagcdn.com/h240/' + item.img + '.png'" :alt="item.name.toLowerCase()" />
                                            </v-list-item-avatar>
                                            <v-list-item-content>
                                                <v-list-item-title v-html="item.name"/>
                                            </v-list-item-content>
                                        </template>
                                    </v-autocomplete>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-select
                                        :label="labels.td"
                                        :items="dni"
                                        name="td"
                                        :readonly="!editMode"
                                        v-model="form.td"
                                        :error-messages="errors.td"
                                        :disabled="loading"
                                        :rules="[rules.required('td')]"
                                        @select="clearErrors('td')"
                                        prepend-icon="mdi-clipboard-text"
                                        item-text="name"
                                        item-value="value">
                                        <template v-slot:selection="{ item }">
                                            <v-flex class="ml-1">{{ item.value }} - {{ item.name }}</v-flex>
                                        </template>
                                        <template v-slot:item="{ item }">
                                            <v-list-item-content>
                                                <v-list-item-title>{{ item.value }} - {{ item.name }}</v-list-item-title>
                                            </v-list-item-content>
                                        </template>
                                    </v-select>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        v-if="form.citizenship === 'CHL' && (form.td === 'CI' || form.td === 'CE')"
                                        :label="labels.nd"
                                        v-model="form.nd"
                                        type="text"
                                        name="nd"
                                        :readonly="!editMode"
                                        :error-messages="errors.nd"
                                        :rules="[rules.required('nd'), rules.equal('nd', 10, 9, 'El')]"
                                        :disabled="loading"
                                        prepend-icon="mdi-ballot"
                                        @input="clearErrors('nd')"
                                        v-mask="rut"/>
                                    <v-text-field
                                        v-else
                                        :label="labels.nd"
                                        v-model="form.nd"
                                        type="text"
                                        name="nd"
                                        :readonly="!editMode"
                                        :error-messages="errors.nd"
                                        :rules="[rules.required('nd'), rules.min('nd', 5, 'El'), rules.max('nd', 20, 'El')]"
                                        :disabled="loading"
                                        prepend-icon="mdi-ballot"
                                        @input="clearErrors('nd')"/>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-checkbox
                                        class="profile-no-expiration mb-1"
                                        v-model="noExpiration"
                                        label="Este documento no tiene fecha de vencimiento"
                                        color="primary"
                                        :disabled="!editMode || loading"
                                        @change="toggleNoExpiration"/>
                                    <v-dialog
                                        ref="menu2"
                                        v-model="menu.exp"
                                        :close-on-content-click="false"
                                        transition="scale-transition"
                                        offset-y
                                        width="290px">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-text-field
                                                :label="labels.exp"
                                                v-model="exp"
                                                name="exp"
                                                prepend-icon="mdi-calendar"
                                                :error-messages="errors.exp"
                                                :rules="expirationRules"
                                                :disabled="loading || noExpiration"
                                                readonly
                                                v-bind="attrs"
                                                v-on="editMode && !noExpiration ? on : {}"/>
                                        </template>
                                        <v-date-picker
                                            v-if="editMode && !noExpiration"
                                            v-model="form.exp"
                                            :active-picker.sync="activePicker"
                                            scrollable
                                            :min="todayDate"
                                            @change="menu.exp = false"/>
                                    </v-dialog>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>

                    <v-card class="profile-section-card profile-documents-card mb-4">
                        <v-card-title class="subtitle-1 font-weight-bold primary--text">
                            <v-icon color="primary" class="mr-2">mdi-folder-multiple-image</v-icon>Documentos
                        </v-card-title>
                        <v-card-text>
                            <v-row>
                                <v-col cols="12" md="4">
                                    <v-card outlined class="profile-document-card">
                                        <v-card-title class="subtitle-2 font-weight-bold">Selfie con documento</v-card-title>
                                        <v-card-actions>
                                            <v-btn text color="primary" :disabled="!hasImage('image')" @click="getImg(2)">
                                                <v-icon left>mdi-eye</v-icon>Ver actual
                                            </v-btn>
                                        </v-card-actions>
                                        <v-card-text v-if="editMode">
                                            <v-file-input
                                                :label="labels.image"
                                                v-model="form.image"
                                                name="image"
                                                accept="image/*"
                                                capture="user"
                                                :error-messages="errors.image"
                                                :rules="imageRules"
                                                :disabled="loading"
                                                prepend-icon="mdi-camera-lock"
                                                @input="clearErrors('image')"
                                                @click:clear="clearImage(2)"
                                                show-size
                                                counter/>
                                        </v-card-text>
                                    </v-card>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-card outlined class="profile-document-card">
                                        <v-card-title class="subtitle-2 font-weight-bold">Documento frontal</v-card-title>
                                        <v-card-actions>
                                            <v-btn text color="primary" :disabled="!hasImage('front')" @click="getImg(3)">
                                                <v-icon left>mdi-eye</v-icon>Ver actual
                                            </v-btn>
                                        </v-card-actions>
                                        <v-card-text v-if="editMode">
                                            <v-file-input
                                                :label="labels.dni"
                                                v-model="form.front"
                                                name="front"
                                                accept="image/*"
                                                capture="environment"
                                                placeholder="Frontal"
                                                :error-messages="errors.front"
                                                :rules="frontRules"
                                                :disabled="loading"
                                                prepend-icon="mdi-card-account-details-star"
                                                @input="clearErrors('front')"
                                                @click:clear="clearImage(3)"
                                                show-size
                                                counter/>
                                        </v-card-text>
                                    </v-card>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-card outlined class="profile-document-card">
                                        <v-card-title class="subtitle-2 font-weight-bold">Documento posterior</v-card-title>
                                        <v-card-actions>
                                            <v-btn text color="primary" :disabled="!hasImage('post')" @click="getImg(4)">
                                                <v-icon left>mdi-eye</v-icon>Ver actual
                                            </v-btn>
                                        </v-card-actions>
                                        <v-card-text v-if="editMode">
                                            <v-file-input
                                                :label="labels.dni"
                                                v-model="form.post"
                                                name="post"
                                                accept="image/*"
                                                capture="environment"
                                                placeholder="Posterior"
                                                :error-messages="errors.post"
                                                :disabled="loading"
                                                prepend-icon="mdi-card-account-details"
                                                @input="clearErrors('post')"
                                                @click:clear="clearImage(4)"
                                                show-size
                                                counter/>
                                        </v-card-text>
                                    </v-card>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>

                    <v-card class="profile-section-card mb-8" v-if="editMode">
                        <v-card-title class="subtitle-1 font-weight-bold primary--text">
                            <v-icon color="primary" class="mr-2">mdi-shield-check</v-icon>Confirmar cambios
                        </v-card-title>
                        <v-card-text>
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-text-field
                                        :label="labels.password"
                                        name="password"
                                        v-model="form.password"
                                        :append-icon="passwordHidden ? 'mdi-eye-off' : 'mdi-eye'"
                                        @click:append="() => (passwordHidden = !passwordHidden)"
                                        :type="passwordHidden ? 'password' : 'text'"
                                        :error-messages="errors.password"
                                        :disabled="loading"
                                        :rules="[rules.req('password')]"
                                        @input="clearErrors('password')"
                                        prepend-icon="mdi-lock"/>
                                </v-col>
                                <v-col cols="12" md="6" class="d-flex align-center">
                                    <v-btn type="submit" :loading="loading" :disabled="loading || !valid" color="primary" large block>
                                        <v-icon left>mdi-database-sync</v-icon>Enviar a revision
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-form>

                <v-dialog v-model="nicknameDialog" persistent scrollable max-width="460px">
                    <v-card class="profile-edit-fixed-dialog">
                        <v-card-title class="primary secondary--text profile-edit-fixed-header" style="word-break: normal">
                            <v-icon color="secondary" class="mr-2">mdi-badge-account</v-icon>
                            Personalizar ID
                            <v-spacer/>
                            <v-btn icon color="secondary" :disabled="savingNickname" @click="nicknameDialog = false">
                                <v-icon>mdi-close</v-icon>
                            </v-btn>
                        </v-card-title>
                        <v-card-text class="profile-edit-fixed-body pt-4">
                            <v-alert dense text type="warning">
                                Solo puedes cambiarlo una vez. Este sera tu identificador publico para pagos y solicitudes.
                            </v-alert>
                            <v-text-field
                                v-model="nicknameForm"
                                label="ID"
                                prefix="$"
                                prepend-icon="mdi-at"
                                :messages="nicknameMessage"
                                :success="nicknameAvailable === true"
                                :error="nicknameAvailable === false"
                                :disabled="savingNickname"
                                @input="queueNicknameCheck"
                                filled/>
                            <div v-if="nicknameSuggestions.length">
                                <v-chip
                                    v-for="item in nicknameSuggestions"
                                    :key="item"
                                    small
                                    color="secondary"
                                    class="primary--text font-weight-bold mr-1 mb-1"
                                    @click="selectNickname(item)">
                                    ${{ item }}
                                </v-chip>
                            </div>
                        </v-card-text>
                        <v-divider/>
                        <v-card-actions class="profile-edit-fixed-footer">
                            <v-btn text color="error" :disabled="savingNickname" @click="nicknameDialog = false">
                                Cancelar
                            </v-btn>
                            <v-spacer/>
                            <v-btn color="primary" :loading="savingNickname" :disabled="savingNickname || nicknameAvailable !== true" @click="saveNickname">
                                Guardar ID
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>

                <v-dialog v-model="documentDialog" persistent scrollable max-width="520px">
                    <v-card class="profile-edit-fixed-dialog">
                        <v-card-title class="primary secondary--text profile-edit-fixed-header" style="word-break: normal">
                            <v-icon color="secondary" class="mr-2">mdi-card-account-details-star</v-icon>
                            Validar documento
                            <v-spacer/>
                            <v-btn icon color="secondary" :disabled="identityLoading" @click="documentDialog = false">
                                <v-icon>mdi-close</v-icon>
                            </v-btn>
                        </v-card-title>
                        <v-card-text class="profile-edit-fixed-body pt-4">
                            <v-alert dense text type="info">
                                Usaremos los datos cargados en tu perfil para confirmar que el documento sea propio.
                            </v-alert>
                            <v-select
                                v-model="documentForm.country"
                                :items="identityCountryItems"
                                item-text="name"
                                item-value="code"
                                label="Pais del documento"
                                filled
                                :disabled="identityLoading"
                                prepend-icon="mdi-earth"/>
                            <v-select
                                v-model="documentForm.document_type"
                                :items="documentTypes"
                                item-text="text"
                                item-value="value"
                                label="Tipo de documento"
                                filled
                                :disabled="identityLoading"
                                prepend-icon="mdi-card-account-details"/>
                        </v-card-text>
                        <v-divider/>
                        <v-card-actions class="profile-edit-fixed-footer">
                            <v-btn text color="error" @click="documentDialog = false" :disabled="identityLoading">
                                Cancelar
                            </v-btn>
                            <v-spacer/>
                            <v-btn color="primary" class="secondary--text" :loading="identityLoading" :disabled="identityActionDisabled || !documentForm.document_type" @click="startIdentity(documentForm)">
                                Continuar
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>

                <v-dialog v-model="view" scrollable max-width="800" width="80%">
                    <v-card>
                        <v-card-title class="text-center headline primary secondary--text d-inline-block" style="color: #ffffff; word-break: normal; width: 100%">{{ title }}</v-card-title>
                        <v-card-text class="pa-4">
                            <img :src="img" :alt="title" width="100%"/>
                        </v-card-text>
                        <v-divider/>
                        <v-card-actions>
                            <v-spacer/>
                            <v-btn color="error" @click="view = false"><v-icon>mdi-close-circle</v-icon>Cerrar</v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
    import axios from 'axios';
    import Form from "../helpers/form";
    import { mask }from "vue-the-mask";

    export default {
        name: "Profile",
        mixins: [ Form ],
        props: [ 'user' ],
        directives: { mask },
        data: () => ({
            loading: true,
            editMode: false,
            noExpiration: false,
            passwordHidden: true,
            photoPreview: null,
            nicknameDialog: false,
            nicknameForm: '',
            nicknameAvailable: null,
            nicknameMessage: '',
            nicknameSuggestions: [],
            nicknameTimer: null,
            savingNickname: false,
            identityLoading: false,
            identityReady: false,
            documentDialog: false,
            documentTypes: [
                { text: 'Documento nacional', value: 'NATIONAL_ID' },
                { text: 'Pasaporte', value: 'PASSPORT' },
                { text: 'Licencia de conducir', value: 'DRIVER_LICENSE' },
                { text: 'Permiso de residencia', value: 'RESIDENCE_PERMIT' }
            ],
            documentForm: {
                country: null,
                document_type: 'PASSPORT'
            },
            fallbackCountries: [
                { name: 'Brasil', code: 'BRA' },
                { name: 'Chile', code: 'CHL' },
                { name: 'Colombia', code: 'COL' },
                { name: 'Ecuador', code: 'ECU' },
                { name: 'Estados Unidos', code: 'USA' },
                { name: 'Mexico', code: 'MEX' },
                { name: 'Panama', code: 'PAN' },
                { name: 'Peru', code: 'PER' },
                { name: 'Venezuela', code: 'VEN' }
            ],
            identity: {
                enabled: false,
                status: null,
                status_label: null,
                verification_url: null,
                documents: []
            },
            labels: {
                dni: 'Foto Documento',
                photo: 'Foto Perfil',
                image: 'Foto Validación',
                citizenship: 'Nacionalidad',
                td: 'Tipo de Documento',
                nd: 'Número de Documento',
                name: 'Nombres',
                lname: 'Apellidos',
                email: 'Correo Electrónico',
                birthday: 'Fecha de Nacimiento',
                exp : 'Fecha de Vencimiento',
                phone: 'Telefono',
                password: 'Contraseña Actual'
            },
            current: {
                photo: null,
                image: null,
                front: null,
                post: null
            },
            form: {
                photo: null,
                image: null,
                front: null,
                post: null,
                citizenship: null,
                td: null,
                nd: null,
                name: null,
                lname: null,
                email: null,
                birthday: (new Date(new Date((new Date()).getFullYear() - 18, (new Date()).getMonth(), (new Date()).getDate()) - (new Date()).getTimezoneOffset() * 60000)).toISOString().substring(0, 10),
                exp : (new Date(new Date((new Date()).getFullYear() + 1, (new Date()).getMonth(), (new Date()).getDate()) - (new Date()).getTimezoneOffset() * 60000)).toISOString().substring(0, 10),
                phone: null,
                password: null
            },
            activePicker: null,
            menu: {
                birthday: false,
                exp: false
            },
            items: [],
            dni: [],
            title: '',
            img: null,
            view: false,
            rut: '########-X'
        }),
        created () {
            this.getUser();
            this.getCountries();
            this.getIdentity();
        },
        watch: {
            user() {
                this.getUser();
            },
            'menu.birthday'(val) {
                val && setTimeout(() => (this.activePicker = 'YEAR'));
            },
            'menu.exp'(val) {
                val && setTimeout(() => (this.activePicker = 'YEAR'));
            },
            'form.citizenship'() {
                if (this.form.citizenship === null || this.form.citizenship === undefined) {
                    this.dni = [];
                } else {
                    this.getDNI(this.form.citizenship);
                }
                if (this.editMode && this.$refs.form) {
                    this.form.td = null;
                    this.form.nd = null;
                    this.$refs.form.resetValidation();
                }
            },
            'form.nd'() {
                if (this.form.nd) {
                    this.form.nd = this.form.nd.toUpperCase();
                }
            },
            'form.photo'(file) {
                this.setPhotoPreview(file);
            },
            nicknameDialog(value) {
                if (value) {
                    this.nicknameForm = this.user.nickname || '';
                    this.checkNickname();
                }
            }
        },
        computed: {
            accountTag() {
                return '$' + (this.user.nickname || (this.user.id ? String(this.user.id).padStart(5, '0') : ''));
            },
            canCustomizeNickname() {
                return !this.user.nickname_customized_at;
            },
            adultMaxDate() {
                return (new Date(new Date((new Date()).getFullYear() - 18, (new Date()).getMonth(), (new Date()).getDate()) - (new Date()).getTimezoneOffset() * 60000)).toISOString().substring(0, 10);
            },
            todayDate() {
                return (new Date(new Date((new Date()).getFullYear(), (new Date()).getMonth(), (new Date()).getDate()) - (new Date()).getTimezoneOffset() * 60000)).toISOString().substring(0, 10);
            },
            birthday() {
                return this.formatDate(this.form.birthday)
            },
            exp() {
                return this.formatDate(this.form.exp)
            },
            expirationRules() {
                return this.noExpiration ? [] : [this.rules.req('exp')];
            },
            photoRules() {
                return this.fileRules('photo');
            },
            imageRules() {
                return this.fileRules('image');
            },
            frontRules() {
                return this.fileRules('front');
            },
            profilePhotoSrc() {
                return this.photoPreview || this.imageUrl(this.current.photo, 'img/photo-default.jpg');
            },
            displayName() {
                return [this.form.name, this.form.lname].filter(Boolean).join(' ').trim();
            },
            statusMeta() {
                if (this.user.status === 103) {
                    return { text: 'Verificado', color: 'success', icon: 'mdi-check-circle' };
                }
                if (this.user.status === 102) {
                    return { text: 'En revision', color: 'info', icon: 'mdi-sync-circle' };
                }
                if (this.user.status === 104) {
                    return { text: 'Bloqueado', color: 'error', icon: 'mdi-close-circle' };
                }

                return { text: 'Sin verificar', color: 'tertiary', icon: 'mdi-alert-circle' };
            },
            identityDocuments() {
                return this.identity && Array.isArray(this.identity.documents) ? this.identity.documents : [];
            },
            approvedIdentityDocuments() {
                return this.identityDocuments.filter(item => item.status === 'approved');
            },
            identityCountryItems() {
                return this.items.length ? this.items : this.fallbackCountries;
            },
            identityProfileMissing() {
                const missing = [];
                const add = (value, label) => {
                    if (value === null || value === undefined || String(value).trim() === '') {
                        missing.push(label);
                    }
                };

                add(this.form.name, 'nombres');
                add(this.form.lname, 'apellidos');
                add(this.form.email, 'correo');
                add(this.form.phone, 'telefono');
                add(this.form.birthday, 'fecha de nacimiento');
                add(this.form.citizenship, 'nacionalidad');
                add(this.form.td, 'tipo de documento');
                add(this.form.nd, 'numero de documento');

                if (!this.noExpiration) {
                    add(this.form.exp, 'fecha de vencimiento o marcar que no tiene vencimiento');
                }

                return missing;
            },
            identityProfileReady() {
                return this.identityProfileMissing.length === 0;
            },
            identityBlockedReason() {
                if (this.editMode) {
                    return 'Guarda tus datos antes de iniciar la validacion de identidad.';
                }

                if (!this.identityProfileReady) {
                    return 'Completa y guarda: ' + this.identityProfileMissing.join(', ') + '.';
                }

                return '';
            },
            identityActionDisabled() {
                return this.identityLoading || !this.identityReady || this.editMode || !this.identityProfileReady;
            },
            identityButtonText() {
                if (this.editMode) {
                    return 'Guarda datos primero';
                }

                if (!this.identityProfileReady) {
                    return 'Completar datos';
                }

                if (this.identity.verification_url) {
                    return this.identity.status_label ? 'Continuar: ' + this.identity.status_label : 'Continuar validacion';
                }

                return this.approvedIdentityDocuments.length ? 'Validar otro documento' : 'Validar documento';
            },
            identityStatusMeta() {
                const status = String(this.identity.status || '').toLowerCase().replace(/[_-]/g, ' ').trim();

                if (status === 'approved') {
                    return { text: this.identity.status_label || 'Aprobada', color: 'success', icon: 'mdi-check-circle' };
                }

                if (['pending', 'in review', 'in progress', 'review', 'awaiting user', 'resubmitted'].indexOf(status) !== -1) {
                    return { text: this.identity.status_label || 'En revision', color: 'warning', icon: 'mdi-progress-clock' };
                }

                if (['declined', 'rejected', 'abandoned', 'expired', 'kyc expired'].indexOf(status) !== -1) {
                    return { text: this.identity.status_label || 'Requiere revision', color: 'error', icon: 'mdi-alert-circle' };
                }

                return { text: this.identity.status_label || 'Sin iniciar', color: 'info', icon: 'mdi-card-account-details' };
            }
        },
        methods: {
            getIdentity() {
                this.identityReady = false;
                this.identityLoading = true;
                axios.get('/api/identity/didit').then(res => {
                    this.identity = Object.assign({}, this.identity, res.data.identity || {});
                }).catch(err => {
                    this.$toast.error(this.errorMessage(err, 'No fue posible cargar la validacion de identidad.'), {icon: 'mdi-alert-circle'});
                }).then(() => {
                    this.identityReady = true;
                    this.identityLoading = false;
                });
            },
            openIdentity() {
                if (!this.identityReady || this.identityLoading) {
                    return;
                }

                if (!this.identityProfileReady) {
                    const reason = this.identityBlockedReason || 'Primero completa y guarda tus datos personales.';
                    this.editMode = true;
                    this.$toast.warning(reason, {icon: 'mdi-alert-circle'});
                    return;
                }

                if (this.identity.verification_url) {
                    this.startIdentity();
                    return;
                }

                this.documentForm.country = this.documentForm.country || this.form.citizenship || this.user.country || null;
                this.documentDialog = true;
            },
            startIdentity(context) {
                if (!this.identityReady || this.identityLoading) {
                    return;
                }

                if (!this.identityProfileReady) {
                    const reason = this.identityBlockedReason || 'Primero completa y guarda tus datos personales.';
                    this.editMode = true;
                    this.documentDialog = false;
                    this.$toast.warning(reason, {icon: 'mdi-alert-circle'});
                    return;
                }

                const payload = context && (context.country !== undefined || context.document_type !== undefined)
                    ? Object.assign({}, context)
                    : {};

                if (!payload.document_type && this.identity.verification_url) {
                    window.location.href = this.identity.verification_url;
                    return;
                }

                if (!payload.document_type) {
                    this.documentDialog = true;
                    return;
                }

                payload.country = payload.country || this.form.citizenship || this.user.country || null;

                this.identityLoading = true;
                axios.post('/api/identity/didit/start', payload).then(res => {
                    const session = res.data.session || {};
                    if (session.url) {
                        window.location.href = session.url;
                    } else {
                        this.getIdentity();
                    }
                }).catch(err => {
                    this.$toast.error(this.errorMessage(err, 'No fue posible iniciar la validacion.'), {icon: 'mdi-alert-circle'});
                }).then(() => {
                    this.documentDialog = false;
                    this.identityLoading = false;
                });
            },
            documentStatusColor(status) {
                if (status === 'approved') return 'success';
                if (status === 'rejected') return 'error';
                return 'warning';
            },
            documentStatusIcon(status) {
                if (status === 'approved') return 'mdi-check-circle';
                if (status === 'rejected') return 'mdi-close-circle';
                return 'mdi-progress-clock';
            },
            getCountries() {
                axios.get('/api/countries').then((res) => {
                    this.items = res.data.items;
                }).catch(err => {
                    this.handleErrors(err.response.data.errors);
                })
            },
            getUser() {
                this.loading = true;
                axios.get('/api/profile').then((res) => {
                    this.current.photo = res.data.user.photo;
                    this.current.image = res.data.user.image;
                    this.current.front = res.data.user.front;
                    this.current.post = res.data.user.post;
                    this.form.photo = null;
                    this.form.image = null;
                    this.form.front = null;
                    this.form.post = null;
                    this.form.citizenship = res.data.user.citizenship;
                    this.form.td = res.data.user.td;
                    this.form.nd = res.data.user.nd;
                    this.form.name = res.data.user.name;
                    this.form.lname = res.data.user.lname;
                    this.form.email = res.data.user.email;
                    if (res.data.user.birthday !== null) {
                        this.form.birthday = res.data.user.birthday;
                    }
                    this.noExpiration = res.data.user.expiration === null;
                    this.form.exp = res.data.user.expiration;
                    this.form.phone = res.data.user.phone;
                    this.form.password = null;
                    this.documentForm.country = this.documentForm.country || res.data.user.citizenship || this.user.country || null;
                    this.photoPreview = null;
                }).catch(err => {
                    this.handleErrors(err.response.data.errors);
                }).then(() => {
                    this.loading = false;
                });
            },
            getDNI(v) {
                if (v === 'MEX' || v === 'USA') {
                    this.dni = [
                        { name: 'Licencia de Conducir', value: 'LIC' },
                        { name: 'Pasaporte', value: 'PAS' }
                    ]
                } else if (v === 'PER') {
                    this.dni = [
                        { name: 'Documento Nacional de Identidad', value: 'DNI' },
                        { name: 'Carnet de Extranjeria', value: 'CE' },
                        { name: 'Licencia de Conducir', value: 'LIC' },
                        { name: 'Permiso Temporal de Permanencia', value: 'PTP' },
                        { name: 'Pasaporte', value: 'PAS' }
                    ]
                } else if (v === 'COL') {
                    this.dni = [
                        { name: 'Cedula de Ciudadania', value: 'CC' },
                        { name: 'Cedula de Extranjeria', value: 'CE' },
                        { name: 'Número de Identificación Tributaria', value: 'NIT' },
                        { name: 'Permiso por Protección Temporal', value: 'PPT' },
                        { name: 'Pasaporte', value: 'PAS' }
                    ]
                } else if (v === 'VEN') {
                    this.dni = [
                        { name: 'Cedula de Identidad', value: 'V' },
                        { name: 'Carnet de Extranjeria', value: 'E' },
                        { name: 'RIF', value: 'J' },
                        { name: 'Pasaporte', value: 'PAS' }
                    ]
                } else if (v === 'CHL') {
                    this.dni = [
                        {name: 'Rol Único Tributario', value: 'RUT'},
                        {name: 'Rol Único Nacional', value: 'RUN'},
                        {name: 'Cedula de Identidad', value: 'CI'},
                        {name: 'Cedula de Extranjeria', value: 'CE'},
                        {name: 'Licencia de Conducir', value: 'LIC'},
                        {name: 'Pasaporte', value: 'PAS'}
                    ]
                } else {
                    this.dni = [
                        { name: 'Cedula de Identidad', value: 'CI' },
                        { name: 'Cedula de Extranjeria', value: 'CE' },
                        { name: 'Licencia de Conducir', value: 'LIC' },
                        { name: 'Pasaporte', value: 'PAS' }
                    ]
                }
            },
            getImg(i) {
                const field = this.imageField(i);
                const input = this.form[field] || this.current[field];

                this.title = this.imageTitle(i);

                if (!input) {
                    return;
                }

                if (typeof input === 'string') {
                    this.img = this.imageUrl(input);
                    this.view = true;
                    return;
                }

                const reader = new FileReader;
                reader.onload = e => {
                    this.img = e.target.result;
                    this.view = true;
                };
                reader.readAsDataURL(input);
            },
            clearImage(i) {
                const field = this.imageField(i);
                this.form[field] = null;

                if (field === 'photo') {
                    this.photoPreview = null;
                }
            },
            imageField(i) {
                if (i === 1) return 'photo';
                if (i === 2) return 'image';
                if (i === 3) return 'front';
                return 'post';
            },
            imageTitle(i) {
                if (i === 1) return 'Foto Principal';
                if (i === 2) return 'Foto Validacion';
                if (i === 3) return 'DNI Frontal';
                return 'DNI Posterior';
            },
            hasImage(field) {
                return !!(this.form[field] || this.current[field]);
            },
            imageUrl(path, fallback = '') {
                if (!path) {
                    return fallback ? '/' + fallback : '';
                }

                return '/' + String(path).replace(/^\/+/, '');
            },
            fileRules(field) {
                return this.current[field] ? [] : [this.rules.req(field)];
            },
            setPhotoPreview(file) {
                this.photoPreview = null;

                if (!file || typeof file === 'string') {
                    return;
                }

                const reader = new FileReader;
                reader.onload = e => {
                    this.photoPreview = e.target.result;
                };
                reader.readAsDataURL(file);
            },
            toggleNoExpiration(value) {
                if (value) {
                    this.form.exp = null;
                    this.menu.exp = false;
                    this.clearErrors('exp');
                } else if (!this.form.exp) {
                    this.form.exp = this.todayDate;
                }
            },
            cancelEdit() {
                this.editMode = false;
                this.clearErrors();
                this.getUser();
            },
            formatDate(date) {
                if (!date) return null

                const [year, month, day] = date.split('-')
                return `${day}/${month}/${year}`
            },
            openNicknameDialog() {
                this.nicknameDialog = true;
            },
            queueNicknameCheck() {
                clearTimeout(this.nicknameTimer);
                this.nicknameTimer = setTimeout(this.checkNickname, 400);
            },
            checkNickname() {
                axios.post('/api/nickname/check', {
                    nickname: this.nicknameForm,
                    name: this.form.name || this.user.name,
                    lname: this.form.lname || this.user.lname
                }).then(res => {
                    this.nicknameSuggestions = res.data.suggestions || [];
                    this.nicknameForm = res.data.nickname || this.nicknameForm;

                    if (!this.nicknameForm) {
                        this.nicknameAvailable = null;
                        this.nicknameMessage = '';
                    } else if (!res.data.valid) {
                        this.nicknameAvailable = false;
                        this.nicknameMessage = 'Usa 4 a 20 caracteres: letras, numeros o guion bajo.';
                    } else if (res.data.available || this.nicknameForm === this.user.nickname) {
                        this.nicknameAvailable = true;
                        this.nicknameMessage = 'ID disponible.';
                    } else {
                        this.nicknameAvailable = false;
                        this.nicknameMessage = 'Ese ID ya esta en uso.';
                    }
                });
            },
            selectNickname(nickname) {
                this.nicknameForm = nickname;
                this.checkNickname();
            },
            saveNickname() {
                this.savingNickname = true;
                axios.post('/api/profile/nickname', {nickname: this.nicknameForm}).then(res => {
                    this.$toast.success(res.data.status, {icon: 'mdi-check-circle'});
                    return this.$store.dispatch('fetchUser');
                }).then(() => {
                    this.nicknameDialog = false;
                    this.getUser();
                }).catch(err => {
                    const msg = err.response && err.response.data && err.response.data.status ? err.response.data.status : 'No fue posible actualizar el ID.';
                    this.$toast.error(msg, {icon: 'mdi-alert-circle'});
                }).then(() => {
                    this.savingNickname = false;
                });
            },
            submit() {
                if (this.$refs.form.validate()) {
                    this.loading = true;

                    let formData = new FormData();

                    this.appendFile(formData, 'photo');
                    this.appendFile(formData, 'image');
                    this.appendFile(formData, 'front');
                    this.appendFile(formData, 'post');
                    formData.append('citizenship', this.form.citizenship);
                    formData.append('td',  this.form.td);
                    formData.append('nd',  this.form.nd);
                    formData.append('name',  this.form.name);
                    formData.append('lname',  this.form.lname);
                    formData.append('email',  this.form.email);
                    formData.append('birthday',  this.form.birthday);
                    formData.append('exp',  this.noExpiration ? '' : this.form.exp);
                    formData.append('no_expiration', this.noExpiration ? '1' : '0');
                    formData.append('phone',  this.form.phone);
                    formData.append('password', this.form.password);

                    axios.post('/api/profile/update', formData, {headers: {'Content-Type': 'multipart/form-data'}})
                        .then((res) => {
                            if (res.data.v === 'info') {
                                this.$toast.warning(res.data.status, {icon: 'mdi-information'});
                            } else {
                                this.$toast.success(res.data.status || 'Tus datos fueron enviados para revision.', { icon: 'mdi-check-circle' });
                                this.$store.dispatch('setUser', res.data);
                                this.editMode = false;
                                this.$emit('success');
                                this.getUser();
                                this.getIdentity();
                            }
                        })
                        .catch(err => {
                            const errors = err.response && err.response.data ? err.response.data.errors : null;
                            if (errors) {
                                this.handleErrors(errors);
                            } else {
                                this.$toast.error('No fue posible actualizar tus datos.', {icon: 'mdi-alert-circle'});
                            }
                        })
                        .then(() => {
                            this.loading = false;
                        });
                }
            },
            appendFile(formData, field) {
                const file = this.form[field];

                if (file && typeof file !== 'string') {
                    formData.append(field, file);
                }
            },
            errorMessage(err, fallback) {
                if (err.response && err.response.data && err.response.data.status) {
                    return err.response.data.status;
                }

                return fallback;
            }
        }
    }
</script>

<style scoped>
.profile-edit-fixed-dialog {
    max-height: 90vh;
    display: flex;
    flex-direction: column;
}

.profile-edit-fixed-header,
.profile-edit-fixed-footer {
    flex: 0 0 auto;
}

.profile-edit-fixed-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
}

.profile-identity-summary,
.profile-identity-document {
    height: 100%;
}

.profile-identity-document {
    border-color: rgba(13, 147, 165, .22) !important;
}

.profile-hero-card,
.profile-section-card,
.profile-document-card {
    border-radius: 8px;
}

.profile-display-name {
    line-height: 1.25;
    word-break: normal;
}

.profile-hero-actions {
    flex: 0 0 auto;
}

@media (max-width: 600px) {
    .profile-container {
        padding-left: 10px;
        padding-right: 10px;
    }

    .profile-main-col {
        padding-left: 0;
        padding-right: 0;
    }

    .profile-hero-card .v-card__text {
        padding: 14px !important;
    }

    .profile-avatar-col,
    .profile-info-col {
        padding-bottom: 6px;
        padding-top: 6px;
    }

    .profile-display-name {
        font-size: 1.2rem !important;
        text-align: center;
    }

    .profile-hero-actions {
        width: 100%;
    }

    .profile-info-col .subtitle-2 {
        text-align: center;
    }

    .profile-info-col .d-flex.flex-column {
        align-items: center;
    }

    .profile-info-col .v-chip {
        max-width: 100%;
    }

    .profile-avatar-col ::v-deep .v-avatar {
        margin-bottom: 8px !important;
    }

    .profile-section-card > .v-card__title {
        align-items: center;
        font-size: .98rem !important;
        line-height: 1.25;
        min-height: 0;
        padding: 12px 14px 6px !important;
        word-break: normal;
    }

    .profile-section-card > .v-card__text {
        padding: 12px 14px 14px !important;
    }

    .profile-section-card .row {
        margin-left: -4px;
        margin-right: -4px;
    }

    .profile-section-card .col,
    .profile-section-card [class*="col-"] {
        padding: 4px;
    }

    .profile-section-card ::v-deep .v-input {
        margin-top: 6px;
    }

    .profile-section-card ::v-deep .v-input__slot {
        min-height: 46px;
    }

    .profile-no-expiration {
        margin-top: 0;
    }

    .profile-no-expiration ::v-deep .v-label {
        font-size: .88rem;
        line-height: 1.25;
    }

    .profile-document-card .v-card__title {
        font-size: .9rem !important;
        line-height: 1.2;
        min-height: 0;
        padding: 10px 12px 0 !important;
        word-break: normal;
    }

    .profile-document-card .v-card__actions,
    .profile-document-card .v-card__text {
        padding: 8px 12px 12px !important;
    }

    .profile-edit-fixed-dialog {
        border-radius: 0 !important;
        max-height: 100vh;
    }

    .profile-edit-fixed-header {
        font-size: 1rem !important;
        line-height: 1.2;
        min-height: 56px;
        padding: 10px 14px !important;
        word-break: normal;
    }

    .profile-edit-fixed-body {
        padding: 14px !important;
    }

    .profile-edit-fixed-footer {
        padding: 10px 14px calc(14px + env(safe-area-inset-bottom)) !important;
    }
}
</style>
